import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Storage key utilized to persist user's drawer open/close preference in localStorage.
 */
export const DRAWER_PREFERENCE_STORAGE_KEY = 'ayb_drawer_open_preference';

/**
 * Service managing layout side drawer visibility state and synchronizing with localStorage preferences.
 */
@Injectable({
  providedIn: 'root',
})
export class DrawerService {
  private readonly _layoutState$: BehaviorSubject<boolean>;

  /**
   * Initializes DrawerService and reads stored user preference from localStorage.
   */
  constructor() {
    const initialPreferenceState = this.readStoredDrawerPreference();
    this._layoutState$ = new BehaviorSubject<boolean>(initialPreferenceState);
  }

  /**
   * Reads user's stored drawer preference from localStorage, defaulting to true on desktop devices.
   *
   * Description:
   *   Retrieves stored boolean string from window.localStorage. If absent, checks window width
   *   and defaults to true for desktop (>= 992px) and false for mobile viewports.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   boolean - Initial drawer open state.
   *
   * Exceptions & Errors:
   *   Gracefully falls back to true if window/localStorage is unavailable or restricted.
   */
  private readStoredDrawerPreference(): boolean {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const storedPreferenceValue = window.localStorage.getItem(
          DRAWER_PREFERENCE_STORAGE_KEY,
        );
        if (storedPreferenceValue !== null) {
          return storedPreferenceValue === 'true';
        }
        return window.innerWidth >= 992;
      }
    } catch (storageError) {
      console.warn(
        '[DrawerService] Could not access localStorage for drawer preference:',
        storageError,
      );
    }
    return true;
  }

  /**
   * Observable stream broadcasting drawer visibility state changes.
   *
   * @returns {Observable<boolean>} Stream of boolean drawer states.
   */
  public get drawerVisibility(): Observable<boolean> {
    return this._layoutState$.asObservable();
  }

  /**
   * Returns current synchronous boolean state of the drawer.
   *
   * @returns {boolean} True if drawer is currently open, false otherwise.
   */
  public get isDrawerOpen(): boolean {
    return this._layoutState$.value;
  }

  /**
   * Updates the drawer visibility state and optionally persists the decision to localStorage.
   *
   * Description:
   *   Emits new boolean state through the internal BehaviorSubject and saves to localStorage
   *   when persistToStorage is true.
   *
   * Parameters:
   *   isOpenState: boolean - New open/close state for the drawer.
   *   persistToStorage: boolean (default true) - Whether to store preference in localStorage.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   Logs warning if localStorage write fails.
   */
  public setDrawerVisibility(
    isOpenState: boolean,
    persistToStorage: boolean = true,
  ): void {
    if (persistToStorage) {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(
            DRAWER_PREFERENCE_STORAGE_KEY,
            String(isOpenState),
          );
        }
      } catch (storageError) {
        console.warn(
          '[DrawerService] Could not write drawer preference to localStorage:',
          storageError,
        );
      }
    }
    this._layoutState$.next(isOpenState);
  }

  /**
   * Toggles the current drawer visibility state and persists the new state.
   *
   * Description:
   *   Inverts current boolean state and saves preference.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public toggleDrawer(): void {
    const nextToggleState = !this._layoutState$.value;
    this.setDrawerVisibility(nextToggleState, true);
  }
}
